import type { SxProps } from '@mui/material';
import { alpha, Box, Grid, InputBase, Input as muiInput, styled } from '@mui/material';
import type { Theme } from '@mui/material/styles';

/** Tailwind class string replacing the old comboboxScenePaperStyle SxProps */
export const COMBOBOX_SCENE_PAPER =
    'bg-white text-c3 font-medium text-sm p-0 uppercase [&_[data-combobox-option]]:px-2.5 [&_[data-combobox-option]]:bg-none';

export const styles = {
    navigation: {
        position: 'relative',
        alignItems: 'start',
        flexDirection: 'column',
        display: 'flex',
        minHeight: '0',
        '& .active': {
            color: (theme: Theme) => theme.palette.primary.main,
        },
        '& a': {
            '&:focus-visible': {
                outline: 'none',
            },
        },
    },
    textOneLine: {
        overflow: 'hidden',
        width: '100%',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '8px',
        color: (theme: Theme) => (theme.palette.mode === 'dark' ? theme.custom.c11 : theme.custom.c22),
        '&:hover': {
            background: (theme: Theme) => theme.palette.secondary.light,
            color: (theme: Theme) => theme.palette.secondary.main,
        },
    },
    textHoverMenu: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 1,
        wordBreak: 'break-all',
        WebkitBoxOrient: 'vertical',
    },
    iconTextRowMenu: {
        gap: '8px',
        cursor: 'pointer',
        padding: '8px 16px',
        borderRadius: '8px',
        alignItems: 'center',
        flexWrap: 'nowrap',
        '&:hover': {
            background: (theme: Theme) => theme.palette.secondary.light,
        },
    },
    cropReact: {
        width: 'max-content',
        height: 'max-content',
        display: 'flex',
        alignItems: 'end',
        maxHeight: '70vh',
        maxWidth: '850px',
    },
    iconPapper: {
        width: '20px',
        height: '20px',
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme: Theme) => theme.palette.secondary.light,
    },
    iconBorder: {
        width: '40px',
        height: '40px',
        cursor: 'pointer',
        borderRadius: '12px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme: Theme) => theme.palette.background.paper,
        border: (theme: Theme) => `1px solid ${theme.palette.secondary.contrastText}`,
    },
    iconRightPosition: {
        top: '1.5em',
        right: '1.5em',
        zIndex: '1200',
        position: 'absolute',
    },
    iconAccent: {
        width: '22px',
        height: '22px',
        padding: '3px',
        cursor: 'pointer',
        borderRadius: '8px',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        verticalAlign: 'middle',
        border: (theme: Theme) => `1px solid ${theme.palette.secondary.main}`,
    },
    scrollVisibleOnHover: {
        marginTop: '40px',
        minHeight: '37vh',
        overflowY: 'scroll',
        borderRadius: '0',
        ':hover': {
            scrollbarWidth: 'auto',
            '::-webkit-scrollbar-thumb': {
                height: '15px',
                background: (theme: Theme) => theme.palette.text.secondary,
            },
            '::-webkit-scrollbar': {
                width: '3px',
            },
        },
    },
    label: {
        padding: '8px',
        paddingLeft: '16px',
        background: (theme: Theme) => theme.palette.background.paper,
        borderRadius: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        marginRight: '16px',
        marginBottom: '16px',
    },
    modalWindow: {
        bgcolor: '#000',
        outline: 'none',
        width: '100%',
        height: '100%',
    },
    modal: {
        height: '100%',
        background: (theme: Theme) => (theme.palette.mode === 'dark' ? '#0a0a0e99' : alpha(theme.palette.background.default, 0.8)),
        backdropFilter: 'blur(15px)',
        overflowY: 'scroll',
        scrollbarWidth: 'none',
        '::-webkit-scrollbar': {
            width: '0',
            height: '0',
        },
        ':hover': {
            scrollbarWidth: 'auto',
            '::-webkit-scrollbar-thumb': {
                height: '15px',
                background: (theme: Theme) => theme.palette.text.secondary,
            },
            '::-webkit-scrollbar': {
                width: '3px',
            },
        },
    },
    link: {
        cursor: 'pointer',
        '&:hover': {
            color: (theme: Theme) => theme.palette.secondary.main,
            transition: 'color .3s ease',
        },
    },
    dropdown: {
        '& .MuiSvgIcon-root': {
            right: '20px',
        },
    },
    dropdownMenu: {
        '& .MuiPaper-root': {
            marginTop: '8px',
            background: (theme: Theme) => theme.palette.secondary.light,
            borderRadius: '16px',
            padding: '8px 0',
            backdropFilter: 'blur(15px)',
            boxShadow: 'none',
            color: (theme: Theme) => theme.palette.secondary.main,
            '& .MuiList-root': {
                background: 'transparent',
                color: (theme: Theme) => theme.palette.secondary.main,
            },
            '& .MuiMenuItem-root.MuiMenuItem-root': {
                background: 'transparent',
                padding: '8px 16px',
                color: (theme: Theme) => theme.palette.info.contrastText,
                ':hover': { color: (theme: Theme) => theme.palette.text.primary },
            },
            '& .MuiMenuItem-root.MuiMenuItem-root.Mui-selected': {
                color: (theme: Theme) => theme.palette.secondary.main,
            },
            '& .Mui-disabled': {
                WebkitTextFillColor: (theme: Theme) => theme.palette.info.contrastText,
                color: (theme: Theme) => theme.palette.info.contrastText,
            },
        },
    },
    notification: {
        position: 'relative',
        cursor: 'pointer',
        padding: '5px 35px 5px 25px',
        marginLeft: '10px',
        borderRadius: '16px',
        background: '#8e8fa717',
    },
    notificationIndicator: {
        top: '15px',
        right: '15px',
        width: '5px',
        height: '5px',
        position: 'absolute',
        borderRadius: '10em',
        background: (theme: Theme) => theme.palette.secondary.main,
    },
    closeIcon: {
        top: '15px',
        right: '15px',
        width: '15px',
        height: '15px',
        position: 'absolute',
    },
    screen: {
        padding: '0 0 0 15px',
    },
    checkBox: {
        border: 'none',
        width: '18px',
        height: '18px',
        svg: {
            fontSize: '1.3rem',
            borderRadius: '6px',
        },
    },
    photoCard: {
        width: '80px',
        height: '80px',
        objectFit: 'cover',
        borderRadius: '16px',
        border: '1px solid transparent',
    },
    textOverFlow: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    editorPaper: {
        alignContent: 'start',
        background: (theme: Theme) => theme.palette.primary.main,
    },
    scrollBarSmall: {
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': {
            width: '6px',
            backgroundColor: (theme: Theme) => theme.custom.c4,
            borderRadius: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
            width: '6px',
            backgroundColor: (theme: Theme) => theme.custom.c16,
            borderRadius: '2px',
        },
        overflowY: 'scroll',
    },
    scrollAutoSmallGrey: {
        '&::-webkit-scrollbar': { width: '7px' },
        '&::-webkit-scrollbar-track': {
            width: '7px',
            backgroundColor: (theme: Theme) => theme.palette.background.default,
        },
        '&::-webkit-scrollbar-thumb': {
            width: '7px',
            backgroundColor: (theme: Theme) => theme.palette.background.default,
            borderRadius: '10px',
        },
        overflowY: 'auto',
        '&:hover': {
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: (theme: Theme) => alpha(theme.palette.secondary.light, 0.1),
            },
        },
    },
    scrollBarDisabled: {
        overflow: 'scroll',
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
    },
    stickyHead: {
        top: '0',
        padding: '14px 0',
        zIndex: '1000',
        position: 'sticky',
        background: (theme: Theme) => theme.palette.background.default,
    },
    searchStyle: {
        '.searchActive': {
            background: (theme: Theme) => theme.palette.success.main,
            color: (theme: Theme) => theme.custom.c1,
        },
        '.searchNotActive': {
            background: (theme: Theme) => alpha(theme.palette.success.main, 0.5),
            color: (theme: Theme) => theme.custom.c11,
        },
    },
    dropdownUniLight: {
        '.MuiInputBase-input': {
            '&.MuiSelect-select': {
                minHeight: 0,
            },
            borderRadius: '12px',
            '&:focus': {
                borderRadius: '12px',
                border: (theme: Theme) => `1px solid ${theme.custom.c46}`,
                background: (theme: Theme) => theme.custom.c44,
            },

            fontSize: '1rem',
            lineHeight: 'normal',
            color: (theme: Theme) => theme.custom.c42,
            border: (theme: Theme) => `1px solid ${theme.custom.c46}`,
            background: (theme: Theme) => theme.custom.c44,
            padding: '13px 16px',
        },
        '.MuiSvgIcon-root': {
            color: (theme: Theme) => theme.palette.info.contrastText,
        },
    },
    dropdownAlt: {
        '& .MuiSelect-select': {
            padding: '0 40px 0 16px',
            fontSize: '1rem',
            lineHeight: '2.5',
            background: (theme: Theme) => theme.palette.secondary.light,
            borderRadius: '12px',
            '&:hover, &:focus, &.Mui-focused': {
                borderRadius: '12px',
                background: (theme: Theme) =>
                    theme.palette.mode === 'dark' ? alpha(theme.palette.secondary.light, 0.08) : theme.palette.text.secondary,
            },
        },
        '& .MuiSvgIcon-root': {
            top: '0.65em',
            right: '16px',
            color: (theme: Theme) => (theme.palette.mode !== 'dark' ? theme.custom.c27 : 'inherit'),
        },
    },
    comboBoxDefault: {
        borderRadius: '12px',
        background: (theme: Theme) => theme.palette.secondary.light,
        color: (theme: Theme) => (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.custom.c27),
        '& :-Webkit-autofill': {
            WebkitBoxShadow: (theme: Theme) =>
                theme.palette.mode === 'dark' ? `inset 0 0 0 50px ${theme.custom.c51}` : `inset 0 0 0 50px ${theme.custom.c46}`,
            WebkitTextFillColor: (theme: Theme) => (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.custom.c27),
        },
        '.MuiOutlinedInput-root': {
            padding: '8.5px 16px',
        },
    },
};

// Light mode matches the header `actions` buttons (issue #1799):
// slate-200 bg with slate-500 icons when inactive, slate-300 borders, red selected segment.
export const getToggleGroupStyles = (isDarkMode: boolean): SxProps<Theme> => ({
    height: '40px',
    borderRadius: '12px',
    '& .MuiToggleButton-root': {
        padding: '4px 8px',
        borderRadius: 0,
        border: (theme: Theme) => `1px solid ${isDarkMode ? alpha(theme.custom.c20, 0.25) : '#CAD5E2'}`,
        '&:first-of-type': {
            borderRadius: '12px 0 0 12px',
        },
        '&:last-of-type': {
            borderRadius: '0 12px 12px 0',
        },
        color: (theme: Theme) => theme.custom.c79,
        backgroundColor: isDarkMode ? 'transparent' : '#E2E8F0',
        '&:hover': {
            zIndex: 1,
            color: (theme: Theme) => theme.palette.secondary.main,
            borderColor: (theme: Theme) => theme.palette.secondary.main,
            background: 'transparent',
            '& svg': {
                color: (theme: Theme) => theme.palette.secondary.main,
            },
        },
        '&.Mui-selected': {
            background: (theme: Theme) => theme.palette.secondary.main,
            border: '1px solid transparent',
            '& svg': {
                color: (theme: Theme) => theme.palette.primary.main,
            },
        },
    },
});

export const LineLabel = styled('div')<{ m?: string }>(props => ({
    borderRadius: '12px',
    padding: '0 12px',
    fontSize: '1.125rem',
    lineHeight: '40px',
    fontWeight: '500',
    margin: props.m || '0',
    background: alpha(props.color || props.theme.palette.secondary.light, props.theme.palette.mode === 'dark' ? 0.15 : 1),
    color: props.theme.palette.mode === 'light' ? props.theme.palette.text.secondary : props.color || props.theme.palette.primary.main,
}));

export const Input = styled(muiInput)(({ theme: _theme }) => ({
    borderRadius: '16px',
    border: '1px solid #c7cbe033',
    padding: '13px 16px',
    fontSize: '1rem',
    marginTop: '15px',
    '&:focus': {
        outline: 'none',
    },
    '& input': {
        padding: '0',
    },
}));

export const EditorInput = styled(muiInput)(props => ({
    position: 'relative',
    padding: '0',
    width: '100%',
    border: 'none',
    fontSize: '1.0625rem',
    background: 'transparent',
    fontFamily: 'Courier New',
    lineHeight: '1.177',
    textAlign: 'left',
    color: props.theme.custom.c47,
    [props.theme.breakpoints.down('elg')]: {
        fontSize: '0.875rem',
        lineHeight: '17px',
    },
    '&:focus': {
        outline: 'none',
    },
    '& .Mui-disabled': {
        WebkitTextFillColor: props.theme.custom.c5,
    },
}));

export interface ModalInputProps {
    color?: string;
    fontSize?: string;
    placeholderColor?: (theme: Theme) => string;
}
export const ModalInput = styled(muiInput)<ModalInputProps>(props => ({
    border: 'none',
    padding: '0',
    width: '100%',
    background: 'transparent',
    color: props.color ? props.color : props.theme.palette.mode === 'dark' ? props.theme.palette.primary.main : props.theme.custom.c16,
    fontSize: props.fontSize ? props.fontSize : '1rem',
    marginTop: '15px',
    lineHeight: '1.5',
    '&:focus': {
        outline: 'none',
    },
    '& input': {
        padding: '0',
        textOverflow: 'ellipsis',
    },
    '& .MuiInputBase-input::placeholder': {
        opacity: '1',
        color: props.placeholderColor
            ? props.placeholderColor(props.theme)
            : props.theme.palette.mode === 'dark'
              ? props.theme.custom.c5
              : props.theme.custom.c3,
    },
}));

export const SelectInput = styled(InputBase)(({ theme, fullWidth }) => ({
    '& .MuiInputBase-input': {
        borderRadius: '16px',
        lineHeight: '1.4',
        position: 'relative',
        fontSize: (theme: Theme) => theme.typography.body1,
        width: fullWidth ? '100%' : '150px',
        fontWeight: 'normal',
        backgroundColor: 'transparent',
        color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.custom.c27,
        '&:focus': {
            backgroundColor: 'transparent',
        },
    },
    '& .MuiSvgIcon-root': {
        color: theme.palette.text.secondary,
        transform: 'rotate(-90deg)',
        width: '0.5em',
        height: '0.5em',
        right: '-0.1em',
        top: '0.2em',
    },
}));

export const InlineSelectInput = styled(InputBase)(({ theme }) => ({
    '.MuiInputBase-input': {
        position: 'relative',
        fontSize: '0.875rem',
        lineHeight: '1',
        color: 'inherit',
        padding: '0 36px 0 12px',
        '&:focus': {
            backgroundColor: 'transparent',
        },
        '&.Mui-disabled': {
            WebkitTextFillColor: theme.custom['ink-muted'],
            color: theme.custom['ink-muted'],
        },
    },

    '& .MuiSelect-select': {
        minHeight: '1rem !important',
    },

    '.MuiSvgIcon-root': {
        color: theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : theme.custom.c3,
        transform: 'rotate(-90deg)',
        width: '0.5em',
        height: '0.5em',
        right: '12px !important',
        top: '0',
        bottom: '0',
        margin: 'auto',

        '&.MuiSelect-iconOpen': {
            transform: 'rotate(90deg)',
        },
    },
}));

export const Divider = styled(Box)(({ theme: _theme }) => ({
    background: '#cecaf947',
    width: '100%',
    height: '2px',
}));

export const TextDivider = styled(Box)(({ theme }) => ({
    background: 'radial-gradient(circle at center, #cecaf947, #fff0)',
    width: '100%',
    height: '2px',
    margin: '46px 0 0 0',
    textAlign: 'center',
    '& *': {
        top: '-10px',
        position: 'relative',
        padding: '0 16px',
        background: theme.custom.c24,
        display: 'inline-block',
    },
}));

export const SceneCount = styled(Box)(({ theme }) => ({
    lineHeight: '40px',
    padding: '0 24px',
    textTransform: 'uppercase',
    fontSize: '0.875rem',
    fontWeight: '700',
    whiteSpace: 'nowrap',
    borderRadius: '36px',
    margin: '0 20px',
    color: theme.palette.mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.secondary,
    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.custom.c7, 0.2) : theme.custom.c12,
}));

export const InputCharacter = styled(muiInput)(({ theme }) => ({
    background: theme.palette.secondary.light,
    padding: '13.5px 16px',
    borderRadius: '16px',
    '&: hover': {
        background: theme.palette.mode === 'dark' ? alpha(theme.palette.secondary.light, 0.08) : theme.palette.secondary.light,
    },
    '&.Mui-error': {
        border: `1px solid ${theme.palette.secondary.main}`,
    },
}));

export const ActionButton = styled(Grid)(({ theme }) => ({
    cursor: 'pointer',
    width: '40px',
    height: '40px',
    margin: '0',
    borderRadius: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.primary.main,
    border: `1px solid ${theme.palette.secondary.main}`,
}));

export const FormInput = styled(Input)(({ theme }) => ({
    background: theme.palette.secondary.light,
    padding: '13.5px 16px',
    border: '1px solid #c7cbe01a',
    borderRadius: '12px',
    lineHeight: 'normal',
    margin: 0,
    alignItems: 'flex-start',
    input: {
        padding: 0,
        height: 'auto',
        backfaceVisibility: 'hidden',
    },
    '&.Mui-error': {
        border: `1px solid ${theme.palette.secondary.main}`,
    },
}));
